/**
 * Generates an ephemeral ECDH key pair using P-256 curve
 * @returns {Promise<CryptoKeyPair>}
 */
export async function generateEphemeralKeyPair() {
  try {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: "ECDH",
        namedCurve: "P-256", // NIST P-256 curve
      },
      true, // extractable
      ["deriveKey", "deriveBits"], // key usages
    );

    return keyPair;
  } catch (err) {
    throw new Error(
      `Failed to generate ECDH key pair: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

/**
 * Converts ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer - ArrayBuffer to convert
 * @returns {string} Base64 encoded string
 */
export function arrayBufferToBase64(buffer) {
  const array = new Uint8Array(buffer);
  return btoa(Array.from(array, (byte) => String.fromCharCode(byte)).join(""));
}

/**
 * Encrypts data using AES-256-GCM
 * @param {object} data - Plaintext data to encrypt
 * @param {string} aesKeyBase64 - AES-256-GCM key (Base64)
 * @returns {Promise<{ ciphertext: string, iv: string }>}
 */
export async function encryptData(data, aesKeyBase64) {
  console.log("🚀 ~ aesKey:", aesKeyBase64);
  console.log("🚀 ~ formdata:", data);
  try {
    const keyBytes = base64ToUint8Array(aesKeyBase64);
    const key = await crypto.subtle.importKey(
      "raw",
      keyBytes.buffer,
      "AES-GCM",
      true,
      ["encrypt"],
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      key,
      new TextEncoder().encode(data),
    );

    return {
      ciphertext: arrayBufferToBase64(ciphertext),
      iv: arrayBufferToBase64(iv.buffer),
    };
  } catch (err) {
    throw new Error(
      `Failed to encrypt data: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

/**
 * Convert base64 string to Uint8Array
 * @param {string} base64
 * @returns {Uint8Array}
 */
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

/**
 * Derives a shared secret from the client's private key and server's public key
 * @param clientPrivateKey - Client's private ECDH key
 * @param serverPublicKey - Server's public ECDH key (Base64 encoded)
 * @returns Promise resolving to the shared secret as ArrayBuffer
 */
export async function deriveSharedSecret(clientPrivateKey, serverPublicKey) {
  try {
    // Import the server's public key
    // serverPublicKey is Base64
    const serverPublicKeyRaw = Uint8Array.from(atob(serverPublicKey), (c) =>
      c.charCodeAt(0),
    );

    const serverKey = await crypto.subtle.importKey(
      "raw",
      serverPublicKeyRaw.buffer,
      { name: "ECDH", namedCurve: "P-256" },
      true,
      [],
    );

    // Derive the shared secret
    const sharedSecret = await window.crypto.subtle.deriveBits(
      {
        name: "ECDH",
        public: serverKey,
      },
      clientPrivateKey,
      256, // 256 bits for AES-256
    );
    console.log("🚀 ~ sharedSecret:", arrayBufferToBase64(sharedSecret));

    return sharedSecret;
  } catch (err) {
    throw new Error(
      `Failed to derive shared secret: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

/**
 * Derives an AES-256-GCM key from the shared secret using HKDF
 * @param sharedSecret - The shared secret from ECDH
 * @returns Promise resolving to the AES-256-GCM key
 */
export async function deriveAESKey(sharedSecret) {
  try {
    // Import the shared secret as a raw key for HKDF
    const baseKey = await crypto.subtle.importKey(
      "raw",
      sharedSecret,
      { name: "HKDF", hash: "SHA-256" },
      false,
      ["deriveKey"],
    );

    const aesKey = await crypto.subtle.deriveKey(
      {
        name: "HKDF",
        hash: "SHA-256",
        salt: new Uint8Array(32), // 32 zero bytes
        info: new TextEncoder().encode("ECDH-AES-256-GCM"),
      },
      baseKey,
      { name: "AES-GCM", length: 256 },
      true, // extractable
      ["encrypt", "decrypt"],
    );

    const rawKey = await crypto.subtle.exportKey("raw", aesKey);
    return arrayBufferToBase64(rawKey);
  } catch (err) {
    throw new Error(
      `Failed to derive AES key: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}
